<footer class="border-border mb-20 border-t bg-gray-100 md:mb-0">
  <div class="mx-auto max-w-7xl px-6 lg:px-12">
    <!--
      SEO inbound-link block: surfaces every /search/:region and /guides/:slug
      page from every page on the site. Closes the orphan-page gap that was
      keeping Google from indexing the Tier 3 content (see HMM-175).
    -->
    <nav
      aria-label="Browse motorhome hire by region and guides"
      class="block border-t border-gray-200 py-8"
    >
      <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <h4 class="mb-3 text-sm font-semibold text-gray-900">Browse by region</h4>
          <ul class="grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-3">
            @for (link of regionLinks; track link.href) {
              <li>
                <a
                  [routerLink]="link.href"
                  class="text-xs !text-gray-600 no-underline transition-colors hover:!text-gray-900 hover:underline"
                  >{{ link.label }}</a
                >
              </li>
            }
          </ul>
        </div>
        <div>
          <h4 class="mb-3 text-sm font-semibold text-gray-900">Motorhome hire guides</h4>
          <ul class="space-y-2">
            @for (link of guideLinks; track link.href) {
              <li>
                <a
                  [routerLink]="link.href"
                  class="text-xs !text-gray-600 no-underline transition-colors hover:!text-gray-900 hover:underline"
                  >{{ link.label }}</a
                >
              </li>
            }
          </ul>
        </div>
      </div>
    </nav>

    <!-- Main Footer Content -->
    <div class="grid grid-cols-1 gap-8 py-12 md:grid md:grid-cols-2 lg:grid-cols-5">
      <!-- Brand Column -->
      <div class="lg:col-span-1">
        <h3 class="text-primary mb-4 text-lg">
          <img
            src="logo-132x132.png"
            width="132"
            height="132"
            alt="HireMyMotorhome Logo"
            class="h-12 w-12 object-contain"
          />
          Hire - My Motorhome
        </h3>
        <p class="text-text-secondary mb-4 text-sm">
          The UK's trusted peer-to-peer motorhome rental marketplace.
        </p>
        <div class="flex gap-3">
          @for (social of socialLinks; track social.label) {
            <a
              [href]="social.href"
              [attr.aria-label]="social.label"
              class="bg-background border-border flex h-9 w-9 items-center justify-center rounded-full border transition-colors hover:border-[#004AAD]"
            >
              @switch (social.icon) {
                @case ('facebook') {
                  <svg [lucideIcon]="FacebookIcon" class="text-text-secondary h-4 w-4"></svg>
                }
                @case ('twitter') {
                  <svg [lucideIcon]="TwitterIcon" class="text-text-secondary h-4 w-4"></svg>
                }
                @case ('instagram') {
                  <svg [lucideIcon]="InstagramIcon" class="text-text-secondary h-4 w-4"></svg>
                }
                @case ('linkedin') {
                  <svg [lucideIcon]="LinkedinIcon" class="text-text-secondary h-4 w-4"></svg>
                }
              }
            </a>
          }
        </div>
      </div>

      <!-- Links Columns -->
      @for (section of footerSections; track section.title) {
        <div>
          <h4 class="mb-4 text-sm">{{ section.title }}</h4>
          <ul class="space-y-3">
            @for (link of section.links; track link.label) {
              <li>
                @if (link.action) {
                  <button
                    (click)="onLinkClick(link.action)"
                    [style.text-decoration]="'none'"
                    class="cursor-pointer text-sm !text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
                  >
                    {{ link.label }}
                  </button>
                } @else if (link.routerLink) {
                  <a
                    [routerLink]="link.href"
                    [style.text-decoration]="'none'"
                    class="text-sm !text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
                  >
                    {{ link.label }}
                  </a>
                } @else {
                  <a
                    [href]="link.href"
                    [style.text-decoration]="'none'"
                    class="text-sm !text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
                  >
                    {{ link.label }}
                  </a>
                }
              </li>
            }
          </ul>
        </div>
      }
    </div>

    <!-- Mobile Simplified Footer -->
    <div class="space-y-4 py-8 text-center md:hidden">
      <div class="flex justify-center gap-4">
        @for (social of socialLinks; track social.label) {
          <a
            [href]="social.href"
            [attr.aria-label]="social.label"
            class="bg-background border-border flex h-9 w-9 items-center justify-center rounded-full border transition-colors hover:border-[#004AAD]"
          >
            @switch (social.icon) {
              @case ('facebook') {
                <svg [lucideIcon]="FacebookIcon" class="text-text-secondary h-4 w-4"></svg>
              }
              @case ('twitter') {
                <svg [lucideIcon]="TwitterIcon" class="text-text-secondary h-4 w-4"></svg>
              }
              @case ('instagram') {
                <svg [lucideIcon]="InstagramIcon" class="text-text-secondary h-4 w-4"></svg>
              }
              @case ('linkedin') {
                <svg [lucideIcon]="LinkedinIcon" class="text-text-secondary h-4 w-4"></svg>
              }
            }
          </a>
        }
      </div>
      <div class="text-text-secondary space-y-2 text-sm">
        <div class="flex justify-center gap-3">
          <a
            routerLink="/terms"
            [style.text-decoration]="'none'"
            class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
            >Terms</a
          >
          <span>·</span>
          <a
            routerLink="/privacy"
            [style.text-decoration]="'none'"
            class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
            >Privacy</a
          >
          <span>·</span>
          <a
            routerLink="/contact"
            [style.text-decoration]="'none'"
            class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
            >Support</a
          >
        </div>
        <p>© {{ currentYear }} HireMyMotorhome.co.uk</p>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div
      class="border-border hidden flex-col items-center justify-between gap-4 border-t py-6 md:flex md:flex-row"
    >
      <div class="text-text-secondary flex flex-wrap items-center gap-4 text-sm">
        <p>© {{ currentYear }} HireMyMotorhome.co.uk</p>
        <span class="hidden md:inline">·</span>
        <a
          routerLink="/terms"
          [style.text-decoration]="'none'"
          class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
        >
          Terms
        </a>
        <span>·</span>
        <a
          routerLink="/privacy"
          [style.text-decoration]="'none'"
          class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
        >
          Privacy
        </a>
        <span>·</span>
        <a
          routerLink="/cookies"
          [style.text-decoration]="'none'"
          class="!text-gray-600 transition-colors hover:!text-gray-900 hover:underline"
        >
          Cookies
        </a>
      </div>
      <div class="text-text-secondary flex items-center gap-4 text-sm">
        <button class="hover:text-foreground flex items-center gap-1 transition-colors">
          <span>🇬🇧</span> English (UK)
        </button>
        <button class="hover:text-foreground transition-colors">£ GBP</button>
      </div>
    </div>
  </div>
</footer>
